import { Component, OnInit, OnDestroy, signal, computed, WritableSignal, Signal } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { Icons, DsButtonVariants, DsTooltipPosition } from '@levelaccess/design-system';
import { NavService } from '../../../../services/navigation/nav.service';
import { INavMenuItem, NavigationItem } from '../../../../services/navigation/nav-structure';
import { UserService } from '../../../../services/user.service';
import { userPreferenceScope, userPreferenceScopeParam } from '../../../../../../shared/constants/user-preferences';
import { IUserPreference, IUserPreferencesServerResponse } from '../../../../../../shared/interfaces/user-preference.interface';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { A11yService } from '../../../../services/a11y.service';
import { TranslateService } from '../../../../translate/translate.service';

@Component({
  selector: 'top-navbar-profile-dropdown',
  templateUrl: './top-navbar-profile-dropdown.component.html',
})
export class TopNavbarProfileDropdownComponent implements OnInit, OnDestroy {
  public readonly Icons: typeof Icons = Icons;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly DsTooltipPosition: typeof DsTooltipPosition = DsTooltipPosition;
  public readonly NavigationItem: typeof NavigationItem = NavigationItem;

  public menuItems$: Observable<INavMenuItem[]>;
  public activeMenuItem$: Observable<INavMenuItem>;

  private navSwitchState: WritableSignal<boolean> = signal<boolean>(false);
  public navSwitchLabel: Signal<'nav_switch_on' | 'nav_switch_off'> = computed((): 'nav_switch_on' | 'nav_switch_off' =>
    this.navSwitchState() ? 'nav_switch_on' : 'nav_switch_off',
  );

  protected suppressSelection: WritableSignal<boolean> = signal<boolean>(false);

  private subscription: Subscription = new Subscription();

  constructor(
    private navService: NavService,
    private userService: UserService,
    private featureFlagService: FeatureFlagService,
    private a11yService: A11yService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.loadNavSwitchStateFromPreferences();

    this.menuItems$ = this.featureFlagService.variation$(FeatureFlagCollection.verticalNavigation, false).pipe(
      switchMap((verticalNavigationEnabled: boolean): Observable<INavMenuItem[]> => {
        const menuItems: NavigationItem[] = [NavigationItem.profile, NavigationItem.logout];

        if (verticalNavigationEnabled) {
          menuItems.push(NavigationItem.nav_switch);
        }

        return this.navService.getTopLevelMenuItems$(menuItems);
      }),
      map((items: INavMenuItem[]): INavMenuItem[] => {
        return items.map((item: INavMenuItem): INavMenuItem => {
          if (item.id === NavigationItem.nav_switch) {
            return {
              ...item,
              onClick: (): void => this.onNavSwitchClick(),
            };
          }
          return item;
        });
      }),
    );

    this.activeMenuItem$ = this.navService.activeTopLevelMenuItem$();
  }

  private loadNavSwitchStateFromPreferences(): void {
    if (!this.userService.isAuthenticated()) {
      return;
    }

    this.subscription.add(
      this.featureFlagService
        .variation$(FeatureFlagCollection.verticalNavigation, false)
        .pipe(
          switchMap((verticalNavigationEnabled: boolean): Observable<IUserPreferencesServerResponse> | [] => {
            if (!verticalNavigationEnabled) {
              return [];
            }

            return this.userService.getUserPreferences(userPreferenceScope.navigationSettings).pipe(take(1));
          }),
        )
        .subscribe({
          next: (response: IUserPreferencesServerResponse): void => {
            const navSwitchPreference: IUserPreference = UserService.findUserPreferenceForParam(
              response,
              userPreferenceScopeParam.verticalNavigationEnabled,
              userPreferenceScope.navigationSettings,
            );

            if (navSwitchPreference) {
              this.navSwitchState.set(navSwitchPreference.value === true);
            }
          },
        }),
    );
  }

  public onNavSwitchClick(): void {
    const verticalNavigationEnabled: boolean = this.featureFlagService.variation(FeatureFlagCollection.verticalNavigation, false);

    if (!verticalNavigationEnabled) {
      return;
    }

    const oldState: boolean = this.navSwitchState();
    const newState: boolean = !oldState;

    // Temporarily suppress selection to prevent the nav switch from being selected
    this.suppressSelection.set(true);

    // Update the signal immediately for responsive UI
    this.navSwitchState.set(newState);

    this.a11yService.setMessage(this.translateService.instant('navigation_switched_announcement'));

    // Restore selection after a brief delay
    setTimeout(() => {
      this.suppressSelection.set(false);
    }, 0);

    // Update user preferences in the background
    this.subscription.add(
      this.userService
        .updateUserPreferences(userPreferenceScope.navigationSettings, {
          [userPreferenceScopeParam.verticalNavigationEnabled]: newState,
        })
        .subscribe({
          error: (): void => {
            // Revert the signal if the API call fails
            this.navSwitchState.set(!newState);
          },
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
